import type {
    FlowConfigViewModel,
    FlowStepViewModel,
    AgentExecutionViewModel,
    LLMConfigOptionsViewModel,
} from "@models/doctype";
import type { AgentExecutionFormData } from "@utils/builders/flowConfig";
import { buildCreateFlowConfigRequest } from "@utils/builders/flowConfig";

/**
 * Builds a CreateFlowConfigRequest with a single agent updated in the target step.
 */
export const buildAgentUpdateRequest = (
    flowConfig: FlowConfigViewModel,
    step: FlowStepViewModel,
    agent: AgentExecutionViewModel,
    updatedAgent: AgentExecutionFormData,
) => {
    const updatedSteps = flowConfig.steps.map((s) => {
        const isTargetStep = s.stepOrder === step.stepOrder;

        if (s.llmStepConfig) {
            const executions = s.llmStepConfig.executions?.map((a) => {
                if (isTargetStep && a.domainKey === agent.domainKey) {
                    return updatedAgent;
                }
                return {
                    domainKey: a.domainKey,
                    userPrompt: a.userPrompt,
                    responseSchema: a.responseSchema,
                    responseSchemaText: JSON.stringify(a.responseSchema, null, 2),
                    systemPrompt: a.systemPrompt || "",
                    modelName: a.modelName || "",
                    configOptions: a.configOptions,
                };
            }) ?? null;

            return {
                stepOrder: s.stepOrder,
                stepType: s.stepType,
                llmStepConfig: {
                    provider: s.llmStepConfig.provider,
                    systemPrompt: s.llmStepConfig.systemPrompt,
                    userPrompt: s.llmStepConfig.userPrompt,
                    modelName: s.llmStepConfig.modelName,
                    configOptions: s.llmStepConfig.configOptions,
                    responseSchema: s.llmStepConfig.responseSchema,
                    executions,
                },
            };
        }

        if (s.ocrStepConfig) {
            return {
                stepOrder: s.stepOrder,
                stepType: s.stepType,
                ocrStepConfig: {
                    provider: s.ocrStepConfig.provider,
                    modelName: s.ocrStepConfig.modelName,
                    ocrOptions: s.ocrStepConfig.ocrOptions,
                },
            };
        }

        return { stepOrder: s.stepOrder, stepType: s.stepType };
    });

    return buildCreateFlowConfigRequest({
        flowBaseId: flowConfig.flowBaseId,
        flowType: flowConfig.flowType,
        flowDescription: flowConfig.flowDescription,
        steps: updatedSteps,
    });
};

/**
 * Builds config options from form state, returning null if no options are set.
 */
export const buildConfigOptionsFromForm = (opts: {
    temperature: number | string;
    topP: number | string;
    topK: number | string;
    maxOutputTokens: number | string;
    thinkingBudget: number | string;
    seed: number | string;
}): LLMConfigOptionsViewModel | null => {
    const configOptions: LLMConfigOptionsViewModel = {};
    if (opts.temperature !== "") configOptions.temperature = Number(opts.temperature);
    if (opts.topP !== "") configOptions.topP = Number(opts.topP);
    if (opts.topK !== "") configOptions.topK = Number(opts.topK);
    if (opts.maxOutputTokens !== "") configOptions.maxOutputTokens = Number(opts.maxOutputTokens);
    if (opts.thinkingBudget !== "") configOptions.thinkingBudget = Number(opts.thinkingBudget);
    if (opts.seed !== "") configOptions.seed = Number(opts.seed);
    return Object.keys(configOptions).length > 0 ? configOptions : null;
};
